import { Email } from '@akpl/shared';

/**
 * Client-side validation for the auth forms. The zod schemas come from `@akpl/shared` where
 * the API shares them (`Email`, the 60-char display name); the password rule is Firebase's
 * own minimum of 6 characters, checked here so the phone never spends a round-trip to learn it.
 *
 * Every issue is reported as an `mobile:auth.errors.*` key, never as a message, so the copy
 * stays in `@akpl/i18n` in both languages.
 */
export const MIN_PASSWORD_LENGTH = 6;
export const MAX_NAME_LENGTH = 60;

export type FieldErrors<F extends string> = Partial<Record<F, string>>;

export function validateEmail(value: string): string | null {
  if (!value.trim()) return 'emailRequired';
  return Email.safeParse(value).success ? null : 'emailInvalid';
}

export function validatePassword(value: string): string | null {
  if (!value) return 'passwordRequired';
  if (value.length < MIN_PASSWORD_LENGTH) return 'passwordShort';
  return null;
}

export function validateName(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'nameRequired';
  if (trimmed.length > MAX_NAME_LENGTH) return 'nameTooLong';
  return null;
}

export interface LoginValues {
  email: string;
  password: string;
}

export function validateLogin(values: LoginValues): FieldErrors<'email' | 'password'> {
  const errors: FieldErrors<'email' | 'password'> = {};
  const email = validateEmail(values.email);
  if (email) errors.email = email;
  const password = validatePassword(values.password);
  if (password) errors.password = password;
  return errors;
}

export interface SignupValues extends LoginValues {
  name: string;
}

export function validateSignup(values: SignupValues): FieldErrors<'name' | 'email' | 'password'> {
  const errors: FieldErrors<'name' | 'email' | 'password'> = { ...validateLogin(values) };
  const name = validateName(values.name);
  if (name) errors.name = name;
  return errors;
}

export function hasErrors(errors: Record<string, string | undefined>): boolean {
  return Object.values(errors).some(Boolean);
}

/** Normalizes an e-mail the way Firebase does before it hits the API (`trim` + lower-case). */
export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}
